/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';
import Loadable from 'react-loadable';

import { IconButton } from '@atlaskit/button/new';
import { cssMap, cx, jsx } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import HistoryIcon from '@atlaskit/icon-lab/core/history';
import AddIcon from '@atlaskit/icon/core/add';
import AgentIcon from '@atlaskit/icon/core/ai-agent';
import ChatIcon from '@atlaskit/icon/core/ai-chat';
import ChartTrendUpIcon from '@atlaskit/icon/core/chart-trend-up';
import CloseIcon from '@atlaskit/icon/core/cross';
import EditIcon from '@atlaskit/icon/core/edit';
import ScorecardIcon from '@atlaskit/icon/core/scorecard';
import { ExitingPersistence, SlideIn } from '@atlaskit/motion';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline, Stack } from '@atlaskit/primitives/compiled';
import { MenuList as AtlasMenuList } from '@atlaskit/side-nav-items/menu-list';
import {
	MenuSection as AtlasMenuSection,
	MenuSectionHeading as AtlasMenuSectionHeading,
} from '@atlaskit/side-nav-items/menu-section';
import { token } from '@atlaskit/tokens';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import type { Agent } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { useActiveMenuSelector } from '@atlassian/conversation-assistant-store/controllers/ui-store';
import { GlobalSideNavigation, type ItemsConfig } from '@atlassian/global-side-navigation';
import { AtlassianRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';
import type {
	Space,
	CreateSpaceResponse,
} from '@atlassian/rovo-spaces/controllers/space-store/types';

import type { Conversation } from '../../common/types';
import { ButtonMenuItem } from '../../common/ui/button-menu-item';
import { List } from '../../common/ui/list';
import { ListItem } from '../../common/ui/list-item';
import { MenuSection } from '../../common/ui/menu-section';
import { MenuSectionHeading } from '../../common/ui/menu-section/menu-section-heading';

import { AgentsMenuItems } from './agents-items';
import { ChatListMenuItems } from './chat-list-items';
import { ErrorBoundary as RovoNavigationErrorBoundary } from './error-boundary';
import { ExpandableMenuItem } from './expandable-menu-item';
import messages from './messages';
import { SideNavContainer } from './side-nav-container';
import { SideNavContent } from './side-nav-content';
import { SpaceMenuItems } from './space-items';
import { type RovoSideNavigationProps, SideNavModes } from './types';
import { useUIConfig } from './ui-config-context';

// this needs to render ABOVE the chat mini-modal
// TODO: revisit this value during integration
const NAV_Z_INDEX = 510;

const RECOMMENDED_SPACES_ID = 'recommended';

const SmallAddIcon = (): JSX.Element => <AddIcon label="" size="small" />;

const renderRecommendedSpacesItem = (
	item: React.ReactNode,
	isSpacesSpotlightEnabled: boolean,
): React.ReactNode => {
	return isSpacesSpotlightEnabled ? (
		<React.Suspense fallback={item}>
			<RovoChatSpacesSpotlight>{item}</RovoChatSpacesSpotlight>
		</React.Suspense>
	) : (
		item
	);
};

const RovoChatSpacesSpotlightPlacement = Loadable({
	loader: () =>
		import(
			/* webpackChunkName: "@atlassian/rovo-chat-spaces-spotlight-placement" */ './rovo-chat-spaces-spotlight/RovoChatSpacesSpotlightPlacement'
		).then((m) => m.RovoChatSpacesSpotlightPlacement),
	loading: () => null,
});

const RovoChatSpacesSpotlight = Loadable({
	loader: () =>
		import(
			/* webpackChunkName: "@atlassian/rovo-chat-spaces-spotlight" */ './rovo-chat-spaces-spotlight/RovoChatSpacesSpotlight'
		).then((m) => m.RovoChatSpacesSpotlight),
	loading: () => null,
});

const styles = cssMap({
	sideNavTrayNonFullscreen: {
		maxWidth: '90%',
	},
	sideNavTrayFullscreen: {
		maxWidth: '50%',
	},
	sideNavContent: {
		position: 'absolute',
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		zIndex: NAV_Z_INDEX,
		overflowX: 'hidden',
	},
	// In the ADS path, `ExpandableMenuItemContent` always indents its children by
	// `expandableMenuItemIndentation` (12px). Counter that with a negative inline-start margin
	// of the same magnitude so the Spaces / Chats subsection headings sit left-flush with the
	// parent's clock icon, matching the Figma design.
	//
	// NOTE: a literal '-12px' is intentional here. ADS has no negative spacing token and
	// Compiled's static extraction requires literal values inside `cssMap`. A unit test in
	// `test.tsx` asserts this value stays equal to `-expandableMenuItemIndentation` so it
	// cannot silently drift if the constant ever changes.
	// @ts-expect-error -- cssMap's spacing property type only allows token strings or '0'/'auto'; no negative spacing token exists in ADS. The literal magnitude is kept in sync with `expandableMenuItemIndentation` by a coupling-guard unit test in `test.tsx`.
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space -- justified above
	adsSubsectionFlush: { marginInlineStart: '-12px' },
	spacesSubsectionHeadingRow: {
		paddingInlineEnd: token('space.050'),
	},
});

const backdropStyles = css({
	backgroundColor: token('elevation.shadow.overflow.perimeter'),
	cursor: 'pointer',
	border: 0,
	padding: 0,
});

type RovoSideNavigationInternalProps = RovoSideNavigationProps & {
	onSelectAgent: (a: Agent) => void;
	onSelectSpace: (space: Space) => void;
	onSelectRecommendedSpaces: () => void;
	selectedSpaceId: string | undefined;
	isRecommendedSpacesSelected: boolean;
	isViewAllSpacesSelected: boolean;
	onSelectConversation: (c: Conversation) => void;
	selectedAgent: Agent | undefined;
	selectedConversation: Conversation | undefined;
	onCreateSpaceClick?: () => void;
};

const RovoSideNavigationInternal = ({
	onClose,
	onNewChatButtonClick,
	onPulseClick,
	onTasksClick,
	conversationListProps,
	agentsListProps,
	canCreateAgents,
	mode,
	cloudId,
	orgId,
	workspaceAri,
	assistanceServiceParams,
	isADSNavigationEnabled = false,
	isNewAllConversationScreenEnabled,
	isSpinnerOverlayConversationIconEnabled,
	isTasksEnabled = false,
	selectedAgent,
	selectedConversation,
	onSelectAgent,
	onSelectConversation,
	onCreateSpaceClick,
	onSelectSpace,
	selectedSpaceId,
	isRecommendedSpacesSelected,
	isViewAllSpacesSelected,
	onSelectRecommendedSpaces,
	onSpaceDelete,
	onSpaceRename,
	onSpaceCreated,
	onViewAllSpacesClick,
}: RovoSideNavigationInternalProps) => {
	const { formatMessage, locale } = useIntl();
	const uiConfig = useUIConfig();

	const isFullscreen = mode === SideNavModes.FULLSCREEN;
	const isSpacesSpotlightEnabled = fg('rovo_chat_spaces_spotlight');

	const renderRecommendedSpacesItemBound = useCallback(
		(item: React.ReactNode) => renderRecommendedSpacesItem(item, isSpacesSpotlightEnabled),
		[isSpacesSpotlightEnabled],
	);

	// must be wrapped in ListItem to prevent a11y violations
	const closeButton = (
		<ListItem>
			<IconButton
				testId="rovo-chat-sidenav-close-button"
				shape="circle"
				icon={CloseIcon}
				appearance="subtle"
				onClick={onClose}
				label={formatMessage(messages.closeSideNavLabel)}
				isTooltipDisabled={false}
			/>
		</ListItem>
	);

	const newChatButton = (
		<ButtonMenuItem
			isADSNavigationEnabled={isADSNavigationEnabled}
			elemBefore={<EditIcon label="" />}
			onClick={onNewChatButtonClick}
		>
			{formatMessage(messages.newChatLabel)}
		</ButtonMenuItem>
	);

	const pulseButton = onPulseClick ? (
		<ButtonMenuItem
			isADSNavigationEnabled={isADSNavigationEnabled}
			testId="rovo-chat-insights-nav-button"
			elemBefore={<ChartTrendUpIcon label="" />}
			onClick={onPulseClick}
		>
			{formatMessage(messages.pulseLabel)}
		</ButtonMenuItem>
	) : null;

	const tasksButton = onTasksClick ? (
		<ButtonMenuItem
			isADSNavigationEnabled={isADSNavigationEnabled}
			elemBefore={<ScorecardIcon label="" />}
			onClick={onTasksClick}
		>
			{formatMessage(messages.tasksLabel)}
		</ButtonMenuItem>
	) : null;

	const sideNavContent = (
		<SideNavContent isADSNavigationEnabled={isADSNavigationEnabled}>
			<ListItem>
				{/* must be wrapped in ListItem and be of role list to prevent a11y violations */}
				<Stack space="space.050" role="list">
					{!isFullscreen && closeButton}
					{newChatButton}
					{pulseButton}
				</Stack>
			</ListItem>
			{uiConfig.elements?.agentsMenu !== false && (
				<ExpandableMenuItem
					isADSNavigationEnabled={isADSNavigationEnabled}
					elemBefore={<AgentIcon label="" />}
					title={formatMessage(messages.agentListLabel)}
					defaultExpanded={!isTasksEnabled}
					suppressListPadding={isTasksEnabled}
					actions={
						canCreateAgents &&
						uiConfig.elements?.createAgentButton !== false && (
							<IconButton
								testId="rovo-chat-sidenav-agent-create"
								icon={SmallAddIcon}
								appearance="subtle"
								spacing="compact"
								label={formatMessage(messages.createAgentLabel)}
								onClick={agentsListProps.onCreateAgentClick}
								isTooltipDisabled={false}
								role="link"
							/>
						)
					}
				>
					<AgentsMenuItems
						{...agentsListProps}
						selectedAgent={selectedAgent}
						onSelectAgent={onSelectAgent}
						cloudId={cloudId}
						isADSNavigationEnabled={isADSNavigationEnabled}
						showBrowseAgents={
							uiConfig.elements?.browseAgentsButton !== false &&
							!fg('rovo_chat_hide_browse_agents_and_search')
						}
					/>
				</ExpandableMenuItem>
			)}
			{tasksButton}
			<ExpandableMenuItem
				isADSNavigationEnabled={isADSNavigationEnabled}
				elemBefore={
					isNewAllConversationScreenEnabled ? <HistoryIcon label="" /> : <ChatIcon label="" />
				}
				title={formatMessage(messages.chatListLabel)}
				defaultExpanded
				// When Spaces is enabled, suppress the children's inline-start padding so the inner
				// Spaces / Chats subsection headings sit left-flush with the parent's clock icon
				// (matching the Figma design). The tasks behaviour is unchanged.
				suppressListPadding={
					isTasksEnabled ||
					FeatureGates.getExperimentValue('rovo_chat_spaces_experiment', 'isEnabled', false)
				}
				actions={
					<IconButton
						testId="rovo-chat-sidenav-chat-create"
						icon={SmallAddIcon}
						appearance="subtle"
						spacing="compact"
						label={formatMessage(messages.newChatLabel)}
						onClick={onNewChatButtonClick}
						isTooltipDisabled={false}
					/>
				}
			>
				{FeatureGates.getExperimentValue('rovo_chat_spaces_experiment', 'isEnabled', false) && (
					<MenuSection isMenuListItem>
						<Inline
							alignBlock="center"
							spread="space-between"
							xcss={styles.spacesSubsectionHeadingRow}
						>
							<MenuSectionHeading label={formatMessage(messages.spacesSectionLabel)}>
								{formatMessage(messages.spacesSectionLabel)}
							</MenuSectionHeading>
							{onCreateSpaceClick && (
								<IconButton
									testId="rovo-chat-sidenav-spaces-create-space"
									icon={SmallAddIcon}
									appearance="subtle"
									spacing="compact"
									label={formatMessage(messages.createSpaceLabel)}
									onClick={onCreateSpaceClick}
									isTooltipDisabled={false}
								/>
							)}
						</Inline>
						<List>
							<SpaceMenuItems
								{...conversationListProps}
								onSelectConversation={onSelectConversation}
								currentConversation={selectedConversation}
								cloudId={cloudId}
								isADSNavigationEnabled={isADSNavigationEnabled}
								product={assistanceServiceParams?.product ?? 'rovo'}
								experienceId={assistanceServiceParams?.experienceId ?? 'ai-mate'}
								isNewAllConversationScreenEnabled={isNewAllConversationScreenEnabled}
								isSpinnerOverlayConversationIconEnabled={isSpinnerOverlayConversationIconEnabled}
								onSelectSpace={onSelectSpace}
								selectedSpaceId={selectedSpaceId}
								onSelectRecommendedSpaces={onSelectRecommendedSpaces}
								isRecommendedSpacesSelected={isRecommendedSpacesSelected}
								isViewAllSpacesSelected={isViewAllSpacesSelected}
								onSpaceDelete={onSpaceDelete}
								onSpaceRename={onSpaceRename}
								onNewChatButtonClick={onNewChatButtonClick}
								onSpaceCreated={onSpaceCreated}
								onViewAllSpacesClick={onViewAllSpacesClick}
								renderRecommendedSpacesItem={renderRecommendedSpacesItemBound}
							/>
						</List>
					</MenuSection>
				)}
				{FeatureGates.getExperimentValue('rovo_chat_spaces_experiment', 'isEnabled', false) ? (
					<MenuSection isMenuListItem>
						<MenuSectionHeading label={formatMessage(messages.chatListLabel)}>
							{formatMessage(messages.chatListLabel)}
						</MenuSectionHeading>
						<List>
							<ChatListMenuItems
								isADSNavigationEnabled={isADSNavigationEnabled}
								{...conversationListProps}
								currentConversation={selectedConversation}
								onSelectConversation={onSelectConversation}
								isNewAllConversationScreenEnabled={isNewAllConversationScreenEnabled}
								isSpinnerOverlayConversationIconEnabled={isSpinnerOverlayConversationIconEnabled}
								cloudId={cloudId}
							/>
						</List>
					</MenuSection>
				) : (
					<ChatListMenuItems
						isADSNavigationEnabled={isADSNavigationEnabled}
						{...conversationListProps}
						currentConversation={selectedConversation}
						onSelectConversation={onSelectConversation}
						isNewAllConversationScreenEnabled={isNewAllConversationScreenEnabled}
						isSpinnerOverlayConversationIconEnabled={isSpinnerOverlayConversationIconEnabled}
						cloudId={cloudId}
					/>
				)}
			</ExpandableMenuItem>
		</SideNavContent>
	);

	if (!isSpacesSpotlightEnabled) {
		return sideNavContent;
	}

	return (
		<React.Suspense fallback={sideNavContent}>
			<RovoChatSpacesSpotlightPlacement
				product={assistanceServiceParams?.product ?? 'rovo'}
				locale={locale}
				orgId={orgId}
				tenantId={cloudId}
				workspaceAri={workspaceAri}
			>
				{sideNavContent}
			</RovoChatSpacesSpotlightPlacement>
		</React.Suspense>
	);
};

const RovoSideNavigationWithStore = (props: RovoSideNavigationProps): JSX.Element => {
	const { formatMessage, locale } = useIntl();
	const uiConfig = useUIConfig();
	const isMiniModal = props.mode === SideNavModes.MINI_MODAL;
	const isSidebar = props.mode === SideNavModes.SIDEBAR;
	const shouldSkipAnimation = props.isADSNavigationEnabled || props.shouldSkipAnimation;
	const shouldRenderGlobalSideNavigation = props.isADSNavigationEnabled;
	const isSpacesSpotlightEnabled = fg('rovo_chat_spaces_spotlight');

	const renderRecommendedSpacesItemBound = useCallback(
		(item: React.ReactNode) => renderRecommendedSpacesItem(item, isSpacesSpotlightEnabled),
		[isSpacesSpotlightEnabled],
	);

	const [selectedAgent, setSelectedAgent] = useState<Agent | undefined>(
		props.agentsListProps.selectedAgent,
	);
	const [selectedConversation, setSelectedConveration] = useState<Conversation | undefined>(
		props.conversationListProps.currentConversation,
	);
	const [selectedSpaceId, setSelectedSpaceId] = useState<string | undefined>(props.selectedSpaceId);
	const [isViewAllSpacesSelected, setIsViewAllSpacesSelected] = useState(false);
	const { onNewChatButtonClick, onViewAllSpacesClick } = props;

	const onNewChat = useCallback<NonNullable<RovoSideNavigationProps['onNewChatButtonClick']>>(
		(event, analyticsEvent) => {
			if (selectedAgent) {
				setSelectedAgent(undefined);
			}
			if (selectedConversation) {
				setSelectedConveration(undefined);
			}
			if (selectedSpaceId) {
				setSelectedSpaceId(undefined);
			}
			setIsViewAllSpacesSelected(false);
			onNewChatButtonClick?.(event, analyticsEvent);
		},
		[selectedAgent, selectedConversation, selectedSpaceId, onNewChatButtonClick],
	);

	// sync external props with internal state
	useEffect(() => {
		if (selectedAgent) {
			setSelectedAgent(undefined);
		}
		setSelectedConveration(props.conversationListProps.currentConversation);
	}, [props.conversationListProps.currentConversation, selectedAgent]);

	useEffect(() => {
		setSelectedSpaceId(props.selectedSpaceId);
		setIsViewAllSpacesSelected(false);
	}, [props.selectedSpaceId]);

	const onSelectAgent = useCallback(
		(a: Agent) => {
			setIsViewAllSpacesSelected(false);
			if (selectedConversation) {
				setSelectedConveration(undefined);
			}
			if (selectedSpaceId) {
				setSelectedSpaceId(undefined);
			}
			setSelectedAgent(a);
			props.agentsListProps.onSelectAgent(a, !!props.isADSNavigationEnabled);
		},
		[selectedConversation, selectedSpaceId, props.agentsListProps, props.isADSNavigationEnabled],
	);

	const onSelectConversation = useCallback(
		(c: Conversation) => {
			setIsViewAllSpacesSelected(false);
			if (selectedAgent) {
				setSelectedAgent(undefined);
			}
			if (selectedSpaceId) {
				setSelectedSpaceId(undefined);
			}
			setSelectedConveration(c);
			props.conversationListProps.onSelectConversation(c, !!props.isADSNavigationEnabled);
		},
		[selectedAgent, selectedSpaceId, props.conversationListProps, props.isADSNavigationEnabled],
	);

	const {
		onSelectSpace: onSelectSpaceProp,
		onSelectRecommendedSpaces: onSelectRecommendedSpacesProp,
		onClose,
		isADSNavigationEnabled,
	} = props;

	const handleSpaceCreated = useCallback(
		(space: CreateSpaceResponse, emojiId?: string) => {
			props.onSpaceCreated?.(space, emojiId);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[props.onSpaceCreated],
	);

	const onSelectSpace = useCallback(
		(space: Space) => {
			setIsViewAllSpacesSelected(false);
			if (selectedAgent) {
				setSelectedAgent(undefined);
			}
			if (selectedConversation) {
				setSelectedConveration(undefined);
			}
			setSelectedSpaceId(space.id);
			onSelectSpaceProp?.(space);
			if (!isADSNavigationEnabled) {
				onClose();
			}
		},
		[selectedAgent, selectedConversation, onSelectSpaceProp, isADSNavigationEnabled, onClose],
	);

	const onSelectRecommendedSpaces = useCallback(() => {
		setIsViewAllSpacesSelected(false);
		if (selectedAgent) {
			setSelectedAgent(undefined);
		}
		if (selectedConversation) {
			setSelectedConveration(undefined);
		}
		setSelectedSpaceId(RECOMMENDED_SPACES_ID);
		onSelectRecommendedSpacesProp?.();
		if (!isADSNavigationEnabled) {
			onClose();
		}
	}, [
		selectedAgent,
		selectedConversation,
		onSelectRecommendedSpacesProp,
		isADSNavigationEnabled,
		onClose,
	]);

	const onViewAllSpaces = useCallback(() => {
		if (selectedAgent) {
			setSelectedAgent(undefined);
		}
		if (selectedConversation) {
			setSelectedConveration(undefined);
		}
		if (selectedSpaceId) {
			setSelectedSpaceId(undefined);
		}
		setIsViewAllSpacesSelected(true);
		onViewAllSpacesClick?.();
	}, [selectedAgent, selectedConversation, selectedSpaceId, onViewAllSpacesClick]);

	// Derive which conversation to highlight from the same UI-store routing signal the main content
	// area uses, rather than from stored local selection state. The main area renders a conversation
	// only when `activeMenu` is a chat menu and no space is selected, or when the open conversation
	// belongs to the currently-selected space; on every other surface (browsing spaces, a selected
	// space with no chat open, recommended spaces, agents, tasks, pulse, ...) no chat is highlighted.
	// Deriving at render (instead of syncing to stored state) means there is no stale highlight to
	// fall out of sync when the surface changes or the host re-sends currentConversation (e.g. on
	// window refocus refetch).
	const [{ activeMenu }] = useActiveMenuSelector();
	const currentConversationForHighlight = props.conversationListProps.currentConversation;
	const isChatMenu = activeMenu === 'conversation-list' || activeMenu === 'none';
	// A chat is highlighted only when a chat is the active surface AND no space row is selected.
	// The space row and the chat highlight are mutually exclusive by construction: selecting a
	// space sets `selectedSpaceId`, while opening a chat (including a space's own child chat)
	// clears it via `onSelectConversation`. So whenever a space row is highlighted, no chat is.
	const isShowingAConversation = isChatMenu && !selectedSpaceId;
	const derivedHighlightedConversation = isShowingAConversation
		? currentConversationForHighlight
		: undefined;

	// Recommended-spaces is highlighted whenever the recommended-spaces surface is active. Derive it
	// from `activeMenu` rather than the local `selectedSpaceId` pseudo-value: the host does not model
	// "recommended" as a space id, so the `props.selectedSpaceId` sync effect would otherwise clobber
	// the locally-set value back to undefined when navigating from a real space to recommended.
	const isRecommendedSpacesSelected = activeMenu === 'recommended-spaces';

	const sideNavContent = useMemo(
		() => (
			<SideNavContainer
				isADSNavigationEnabled={props.isADSNavigationEnabled ?? false}
				testId={props.testId}
				maxHeight={props.overrideBoundingBox?.maxHeight}
				maxWidth={props.overrideBoundingBox?.maxWidth}
				zIndex={NAV_Z_INDEX}
				isMiniModal={isMiniModal}
			>
				<RovoNavigationErrorBoundary
					experienceName={ExperienceName.LOAD_PANEL}
					onClose={props.onClose}
				>
					<RovoSideNavigationInternal
						{...props}
						onNewChatButtonClick={onNewChat}
						onSelectAgent={onSelectAgent}
						onSelectConversation={onSelectConversation}
						selectedAgent={selectedAgent}
						selectedConversation={derivedHighlightedConversation}
						onSelectSpace={onSelectSpace}
						onSelectRecommendedSpaces={onSelectRecommendedSpaces}
						selectedSpaceId={selectedSpaceId}
						isRecommendedSpacesSelected={isRecommendedSpacesSelected}
						isViewAllSpacesSelected={isViewAllSpacesSelected}
						onCreateSpaceClick={props.onCreateSpaceClick}
						onSpaceCreated={handleSpaceCreated}
						onViewAllSpacesClick={onViewAllSpaces}
					/>
				</RovoNavigationErrorBoundary>
			</SideNavContainer>
		),
		[
			props,
			selectedAgent,
			derivedHighlightedConversation,
			selectedSpaceId,
			isRecommendedSpacesSelected,
			isViewAllSpacesSelected,
			onNewChat,
			onSelectAgent,
			onSelectConversation,
			onSelectSpace,
			onSelectRecommendedSpaces,
			onViewAllSpaces,
			handleSpaceCreated,
			isMiniModal,
		],
	);

	const globalNavContent = useMemo(() => {
		const items: ItemsConfig = {
			'chat.sidebar.button': {
				type: 'button',
				elemBefore: <EditIcon label="" />,
				label: formatMessage(messages.newChatLabel),
				onClick: onNewChat,
			},

			...(props.onPulseClick && {
				'pulse.sidebar.button': {
					type: 'button' as const,
					testId: 'rovo-chat-insights-nav-button',
					elemBefore: <ChartTrendUpIcon label="" />,
					label: formatMessage(messages.pulseLabel),
					onClick: props.onPulseClick,
				},
			}),

			...(uiConfig.elements?.agentsMenu !== false && {
				'chat-agents.sidebar.expand': {
					type: 'expand',
					elemBefore: <AgentIcon label="" />,
					isDefaultExpanded: !props.isTasksEnabled,
					onExpansionToggle: (isExpanded) => !isExpanded,
					label: formatMessage(messages.agentListLabel),
					...(props.canCreateAgents &&
						uiConfig.elements?.createAgentButton !== false && {
							elemAfter: (
								<IconButton
									testId="rovo-chat-sidenav-agent-create"
									icon={SmallAddIcon}
									appearance="subtle"
									spacing="compact"
									label={formatMessage(messages.createAgentLabel)}
									onClick={props.agentsListProps.onCreateAgentClick}
									isTooltipDisabled={false}
									role="link"
								/>
							),
						}),
					children: (
						<AgentsMenuItems
							{...props.agentsListProps}
							selectedAgent={selectedAgent}
							onSelectAgent={onSelectAgent}
							cloudId={props.cloudId}
							isADSNavigationEnabled
							showBrowseAgents={
								uiConfig.elements?.browseAgentsButton !== false &&
								!fg('rovo_chat_hide_browse_agents_and_search')
							}
						/>
					),
				},
			}),
			...(props.onTasksClick && {
				'tasks.sidebar.button': {
					type: 'button' as const,
					elemBefore: <ScorecardIcon label="" />,
					label: formatMessage(messages.tasksLabel),
					onClick: props.onTasksClick,
				},
			}),

			'chat-history.sidebar.expand': {
				type: 'expand',
				elemBefore: props.isNewAllConversationScreenEnabled ? (
					<HistoryIcon label="" />
				) : (
					<ChatIcon label="" />
				),
				isDefaultExpanded: true,
				onExpansionToggle: (isExpanded) => !isExpanded,
				label: formatMessage(messages.chatListLabel),
				elemAfter: (
					<IconButton
						testId="rovo-chat-sidenav-chat-create"
						icon={SmallAddIcon}
						appearance="subtle"
						spacing="compact"
						label={formatMessage(messages.newChatLabel)}
						onClick={onNewChat}
						isTooltipDisabled={false}
					/>
				),
				children: FeatureGates.getExperimentValue(
					'rovo_chat_spaces_experiment',
					'isEnabled',
					false,
				) ? (
					// Wrap both subsections with a negative inline-start margin so the subsection
					// headings align with the parent's clock icon (counteracting the indentation
					// applied by `AtlasExpandableMenuItemContent`).
					<Box xcss={styles.adsSubsectionFlush}>
						<AtlasMenuSection isMenuListItem>
							<Inline alignBlock="center" spread="space-between">
								<AtlasMenuSectionHeading>
									{formatMessage(messages.spacesSectionLabel)}
								</AtlasMenuSectionHeading>
								{props.onCreateSpaceClick && (
									<IconButton
										testId="rovo-chat-sidenav-spaces-create-space"
										icon={SmallAddIcon}
										appearance="subtle"
										spacing="compact"
										label={formatMessage(messages.createSpaceLabel)}
										onClick={props.onCreateSpaceClick}
										isTooltipDisabled={false}
									/>
								)}
							</Inline>
							<AtlasMenuList>
								<SpaceMenuItems
									{...props.conversationListProps}
									onSelectConversation={onSelectConversation}
									currentConversation={derivedHighlightedConversation}
									cloudId={props.cloudId}
									isADSNavigationEnabled
									product={props.assistanceServiceParams?.product ?? 'rovo'}
									experienceId={props.assistanceServiceParams?.experienceId ?? 'ai-mate'}
									isNewAllConversationScreenEnabled={props.isNewAllConversationScreenEnabled}
									isSpinnerOverlayConversationIconEnabled={
										props.isSpinnerOverlayConversationIconEnabled
									}
									onSelectSpace={onSelectSpace}
									selectedSpaceId={selectedSpaceId}
									onSelectRecommendedSpaces={onSelectRecommendedSpaces}
									isRecommendedSpacesSelected={isRecommendedSpacesSelected}
									onSpaceDelete={props.onSpaceDelete}
									onSpaceRename={props.onSpaceRename}
									onNewChatButtonClick={onNewChat}
									onSpaceCreated={handleSpaceCreated}
									onViewAllSpacesClick={onViewAllSpaces}
									isViewAllSpacesSelected={isViewAllSpacesSelected}
									renderRecommendedSpacesItem={renderRecommendedSpacesItemBound}
								/>
							</AtlasMenuList>
						</AtlasMenuSection>
						<AtlasMenuSection isMenuListItem>
							<AtlasMenuSectionHeading>
								{formatMessage(messages.chatListLabel)}
							</AtlasMenuSectionHeading>
							<AtlasMenuList>
								<ChatListMenuItems
									{...props.conversationListProps}
									isADSNavigationEnabled
									currentConversation={derivedHighlightedConversation}
									onSelectConversation={onSelectConversation}
									isNewAllConversationScreenEnabled={props.isNewAllConversationScreenEnabled}
									isSpinnerOverlayConversationIconEnabled={
										props.isSpinnerOverlayConversationIconEnabled
									}
									cloudId={props.cloudId}
								/>
							</AtlasMenuList>
						</AtlasMenuSection>
					</Box>
				) : (
					<ChatListMenuItems
						{...props.conversationListProps}
						isADSNavigationEnabled
						currentConversation={derivedHighlightedConversation}
						onSelectConversation={onSelectConversation}
						isNewAllConversationScreenEnabled={props.isNewAllConversationScreenEnabled}
						isSpinnerOverlayConversationIconEnabled={props.isSpinnerOverlayConversationIconEnabled}
						cloudId={props.cloudId}
					/>
				),
			},
		};

		return (
			<AtlassianRelayEnvironmentProvider>
				<GlobalSideNavigation
					app="rovoChat"
					appLabel="Rovo Chat"
					forYou={{}}
					items={items}
					disableMenuCustomization
				/>
			</AtlassianRelayEnvironmentProvider>
		);
	}, [
		formatMessage,
		props,
		selectedAgent,
		derivedHighlightedConversation,
		selectedSpaceId,
		isRecommendedSpacesSelected,
		isViewAllSpacesSelected,
		onNewChat,
		onSelectAgent,
		onSelectConversation,
		onSelectSpace,
		onSelectRecommendedSpaces,
		onViewAllSpaces,
		handleSpaceCreated,
		renderRecommendedSpacesItemBound,
		uiConfig,
	]);

	if (shouldRenderGlobalSideNavigation) {
		if (!isSpacesSpotlightEnabled) {
			return <React.Fragment>{globalNavContent}</React.Fragment>;
		}

		return (
			<React.Suspense fallback={globalNavContent}>
				<RovoChatSpacesSpotlightPlacement
					product={props.assistanceServiceParams?.product ?? 'rovo'}
					locale={locale}
					tenantId={props.cloudId}
				>
					{globalNavContent}
				</RovoChatSpacesSpotlightPlacement>
			</React.Suspense>
		);
	}

	if (shouldSkipAnimation) {
		return <React.Fragment>{sideNavContent}</React.Fragment>;
	}

	return (
		<React.Fragment>
			<ExitingPersistence>
				{props.isOpen && (
					<React.Fragment>
						<button
							type="button"
							aria-label={formatMessage(messages.closeSideNavLabel)}
							css={[
								styles.sideNavContent,
								backdropStyles,
								isMiniModal && styles.borderRadiusMiniModal,
							]}
							onClick={props.onClose}
						/>
						<Box
							xcss={cx(
								styles.sideNavContent,
								isMiniModal || isSidebar
									? styles.sideNavTrayNonFullscreen
									: styles.sideNavTrayFullscreen,
							)}
						>
							<SlideIn fade="inout" enterFrom="left" exitTo="left" duration="medium">
								{(animationProps) => (
									<div
										{...animationProps}
										/* eslint-disable @atlaskit/ui-styling-standard/enforce-style-prop */
										style={{
											...animationProps.style,
											height: '100%',
										}}
										/* eslint-enable @atlaskit/ui-styling-standard/enforce-style-prop */
									>
										{sideNavContent}
									</div>
								)}
							</SlideIn>
						</Box>
					</React.Fragment>
				)}
			</ExitingPersistence>
		</React.Fragment>
	);
};

export function RovoSideNavigation(props: RovoSideNavigationProps): JSX.Element {
	return <RovoSideNavigationWithStore {...props} />;
}
